import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { istanbulDayString, shiftIsoDay, t, type MessageKey } from '@aquabox/shared';
import {
  useClearDayAssignment,
  useDayAssignments,
  useSetDayAssignment,
  type DayRosterResponse,
  type DaySource,
  type DayVehicleRow,
} from '../api/day-assignments';
import { ApiError } from '../api/client';
import { useDrivers } from '../api/queries';
import { useToast } from '../components/Toast';
import { Badge, type BadgeTone } from '../components/Pills';

const TZ = 'Europe/Istanbul';

/** Sunucunun gün yazım hata kodları (409/400). Bilinmeyen kod genel hataya düşer. */
const DAY_ERROR_CODES = new Set([
  'PAST_DAY_LOCKED',
  'DAY_DRIVER_TAKEN',
  'DAY_VEHICLE_TAKEN',
  'DAY_OUT_OF_RANGE',
  'DRIVER_INACTIVE',
  'VEHICLE_INACTIVE',
]);

const SOURCE_KEYS: Record<DaySource, MessageKey> = {
  trip: 'assign.roster.source.trip',
  assignment: 'assign.roster.source.assignment',
  default: 'assign.roster.source.default',
};

const SOURCE_TONE: Record<DaySource, BadgeTone> = {
  trip: 'blue',
  assignment: 'green',
  default: 'neutral',
};

/** Bilinen gün kodu → i18n; bilinmeyen kod ya da ağ hatası → genel metin (sunucu metni gösterilmez). */
function errorText(err: unknown): string {
  if (err instanceof ApiError && err.code && DAY_ERROR_CODES.has(err.code)) {
    return t(`assign.dayError.${err.code}` as MessageKey);
  }
  return t('error.internal');
}

/**
 * Araçlar — "Bugünün araç ve şoförleri" kartı (A1, `GET/PUT/DELETE /api/day-assignments`).
 *
 * Her aktif araç için günün şoförü, kaynağı (sefer / görevlendirme / zimmet), yedekler, çelişki
 * rozeti ve akşam geçişinde aracını kaybeden şoförler gösterilir. Şoför seçimi görevlendirme
 * yazar. YAZMA GÖVDESİ: satırın kendi günü (`assignments[].day`) kullanılır, sekmenin günü
 * değil — akşam görünümünde etkin satır yarından gelebilir (B4 NOT-1).
 *
 * Yalnız OWNER/MANAGER: çağıran (Fleet) rolü kontrol eder; API de aynı rolleri kabul eder.
 */
export function DayRosterCard() {
  const toast = useToast();
  const today = istanbulDayString(new Date(), TZ);
  const tomorrow = shiftIsoDay(today, 1);
  const [which, setWhich] = useState<'today' | 'tomorrow'>('today');
  const day = which === 'today' ? today : tomorrow;

  const roster = useDayAssignments(day);
  const drivers = useDrivers();
  const setDay = useSetDayAssignment();
  const clearDay = useClearDayAssignment();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [rematchWarn, setRematchWarn] = useState(false);

  const busy = setDay.isPending || clearDay.isPending;
  const data = roster.data;
  const driverName = new Map(
    (drivers.data?.items ?? []).map((d) => [d.id, d.user.fullName] as const),
  );
  const activeDrivers = (drivers.data?.items ?? []).filter((d) => d.isActive && d.user.isActive);

  /**
   * Araç şoförünü değiştirir. Birden çok çağrı (sil + yaz) olabilir: `rematchFailed` birleşir;
   * yazım yarıda kalırsa silinen eski satır geri yazılır, liste yenilenir, kullanıcıya hata gösterilir.
   */
  async function changeDriver(row: DayVehicleRow, nextDriverId: string): Promise<void> {
    if (!data) return;
    setError(null);
    // Bu araca bağlı görevlendirme satırı (yedek değil). Varsa yazım onun gününe gider.
    const existing = data.assignments.find((a) => a.vehicleId === row.vehicleId && !a.isBackup);
    // Yeni şoförün etkin satırı (başka araçta/yedekte olabilir) da gün kaynağıdır (akşam görünümü).
    const incoming = nextDriverId
      ? data.assignments.find((a) => a.driverId === nextDriverId)
      : undefined;
    // Yazma günü: motorun uygulayacağı gün = üçünün EN GEÇ olanı (YYYY-MM-DD karşılaştırması).
    // Eski satır kendi gününde silinir; yeni satır bu günde yazılır.
    const rowDay = [existing?.day, incoming?.day, data.day]
      .filter((d): d is string => d !== undefined)
      .reduce((latest, d) => (d > latest ? d : latest));
    let rematchFailed: boolean;
    let restoreFailed = false;
    try {
      if (nextDriverId === '') {
        if (!existing) return;
        const r = await clearDay.mutateAsync({ day: existing.day, driverId: existing.driverId });
        rematchFailed = r.rematchFailed === true;
        toast(t('dayRoster.cleared', { plate: row.plate }));
      } else {
        if (existing && existing.driverId !== nextDriverId) {
          // Araç eski satırla dolu: PUT (DAY_VEHICLE_TAKEN) çakışmasını önlemek için önce sil.
          const r = await clearDay.mutateAsync({ day: existing.day, driverId: existing.driverId });
          rematchFailed = r.rematchFailed === true;
          try {
            const s = await setDay.mutateAsync({
              day: rowDay,
              driverId: nextDriverId,
              vehicleId: row.vehicleId,
            });
            rematchFailed = rematchFailed || s.rematchFailed === true;
          } catch (err) {
            // Yeni yazım düştü: silinen eski satır geri yazılır ki araç boş kalmasın. Geri yazma
            // da düşerse kullanıcıya ayrı uyarı (araç elle atanmalı); hata yutulmaz.
            const restored = await setDay
              .mutateAsync({
                day: existing.day,
                driverId: existing.driverId,
                vehicleId: row.vehicleId,
              })
              .then(
                () => true,
                () => false,
              );
            restoreFailed = !restored;
            throw err;
          }
        } else {
          const s = await setDay.mutateAsync({
            day: rowDay,
            driverId: nextDriverId,
            vehicleId: row.vehicleId,
          });
          rematchFailed = s.rematchFailed === true;
        }
        toast(t('dayRoster.saved', { plate: row.plate }));
      }
      setError(null);
      setRematchWarn(rematchFailed);
    } catch (err) {
      setError(restoreFailed ? t('dayRoster.restoreFailed') : errorText(err));
    } finally {
      // Başarıda da hatada da listeyi sunucudan tazele (kısmi yazım görünmesin).
      await qc.invalidateQueries({ queryKey: ['day-assignments'] });
    }
  }

  function renderVehicle(row: DayVehicleRow) {
    const existing = data?.assignments.find((a) => a.vehicleId === row.vehicleId && !a.isBackup);
    // Görevlendirme dışı (sefer/zimmet) şoför, görevlendirme satırı olmadan "boşalta" ile
    // kaldırılamaz: o seçenek yalnız görevlendirme satırı varken açılır.
    const clearable = existing !== undefined;
    const sourceKey = row.source ? SOURCE_KEYS[row.source] : 'assign.roster.noDriver';
    const sourceTone = row.source ? SOURCE_TONE[row.source] : 'red';
    const readOnly = data?.readOnly === true;
    return (
      <div
        key={row.vehicleId}
        data-testid={`day-roster-row-${row.vehicleId}`}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.4fr auto',
          alignItems: 'center',
          gap: 10,
          padding: '8px 0',
          borderTop: '1px solid var(--border)',
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 14 }}>{row.plate}</span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <select
            value={row.driverId ?? ''}
            disabled={readOnly || busy}
            aria-label={`${row.plate} · ${t('dayRoster.colDriver')}`}
            data-testid={`day-roster-select-${row.vehicleId}`}
            onChange={(e) => void changeDriver(row, e.target.value)}
            style={{ height: 38 }}
          >
            <option value="" disabled={!clearable && row.driverId !== null}>
              {t('dayRoster.noDriverOption')}
            </option>
            {/* Mevcut şoför aktif listede değilse (pasif/silinmiş) yine de geçerli bir seçenek olarak
                görünür; yoksa tarayıcı ilk seçeneği gösterir ve durum yanıltır. */}
            {row.driverId && !activeDrivers.some((d) => d.id === row.driverId) ? (
              <option value={row.driverId}>
                {driverName.get(row.driverId) ?? t('assign.roster.noDriver')}
              </option>
            ) : null}
            {activeDrivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.user.fullName}
              </option>
            ))}
          </select>
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          <span data-testid={`day-roster-source-${row.vehicleId}`} data-source={row.source ?? ''}>
            <Badge tone={sourceTone}>{t(sourceKey)}</Badge>
          </span>
          {row.effectiveDay && data && row.effectiveDay !== data.day ? (
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
              {t('dayRoster.fromTomorrow')}
            </span>
          ) : null}
          {row.conflict ? (
            <span data-testid={`day-roster-conflict-${row.vehicleId}`}>
              <Badge tone="red">{t('assign.roster.conflict')}</Badge>
            </span>
          ) : null}
        </span>
      </div>
    );
  }

  const backupNames = (data?.backups ?? []).map((b) => driverName.get(b.driverId) ?? null);
  const lostNames = (data?.lostDrivers ?? []).map((l) => driverName.get(l.driverId) ?? null);

  return (
    <section
      data-testid="day-roster-card"
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 className="h2" style={{ margin: 0 }}>
          {t('dayRoster.title')}
        </h2>
        <span style={{ flex: 1 }} />
        <div role="tablist" style={{ display: 'flex', gap: 4 }}>
          {(
            [
              ['today', 'dayRoster.tabToday'],
              ['tomorrow', 'dayRoster.tabTomorrow'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={which === key}
              data-testid={`day-roster-tab-${key}`}
              className={which === key ? 'btn btn-primary' : 'btn'}
              onClick={() => setWhich(key)}
            >
              {t(label)}
            </button>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
        {t('dayRoster.hint')}
      </p>

      {rematchWarn ? (
        <div
          role="alert"
          data-testid="day-roster-rematch-warning"
          style={{ fontSize: 13, color: 'var(--warning)' }}
        >
          {t('dayRoster.rematchFailed')}
        </div>
      ) : null}
      {error ? (
        <p role="alert" style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
          {error}
        </p>
      ) : null}

      {roster.isPending ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          {t('common.loading')}
        </p>
      ) : roster.isError ? (
        <p role="alert" style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
          {t('dayRoster.loadError')}
        </p>
      ) : data ? (
        <RosterBody
          data={data}
          renderVehicle={renderVehicle}
          backupNames={backupNames}
          lostNames={lostNames}
        />
      ) : null}
    </section>
  );
}

function RosterBody({
  data,
  renderVehicle,
  backupNames,
  lostNames,
}: {
  data: DayRosterResponse;
  renderVehicle: (row: DayVehicleRow) => ReactNode;
  backupNames: (string | null)[];
  lostNames: (string | null)[];
}) {
  const names = (list: (string | null)[]) => list.filter((n): n is string => n !== null).join(', ');
  return (
    <>
      {data.readOnly ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
          {t('dayRoster.readOnly')}
        </p>
      ) : null}
      {data.vehicles.length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0 }}>
          {t('dayRoster.noVehicles')}
        </p>
      ) : (
        <div>{data.vehicles.map((v) => renderVehicle(v))}</div>
      )}
      <div
        data-testid="day-roster-backups"
        style={{ fontSize: 13, color: 'var(--text-secondary)' }}
      >
        <strong>{t('dayRoster.backups')}: </strong>
        {backupNames.length > 0 ? names(backupNames) : t('dayRoster.noBackups')}
      </div>
      {data.lostDrivers.length > 0 ? (
        <div data-testid="day-roster-lost" style={{ fontSize: 13, color: 'var(--warning)' }}>
          {t('dayRoster.lostDrivers', { names: names(lostNames) })}
        </div>
      ) : null}
    </>
  );
}
